import { useCallback, useMemo, useRef } from 'react';
import { Box, CircularProgress, IconButton, styled, Typography } from '@mui/material';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import { useDevices, useLocalVideoToggle } from 'hooks';
import { VideoCameraIcon, VideoCameraOffIcon } from 'assets';
import { ControlTooltip } from 'components';

import styles from './ToggleVideoButton.module.scss';

type ToggleVideoButtonProps = {
  disabled?: boolean;
  className?: string;
  iconClassName?: string;
  withTooltip?: boolean;
  withLabel?: boolean;
};

const VideoIcon = styled(VideoCameraIcon)(() => ({
  color: '#fff',
}));

const VideoOffIcon = styled(VideoCameraOffIcon)(() => ({
  color: '#fff',
}));

export function ToggleVideoButton({
  disabled,
  className,
  iconClassName,
  withTooltip,
  withLabel,
}: ToggleVideoButtonProps) {
  const { t, i18n } = useTranslation('providerRoom');
  const isRtl = i18n.dir() === 'rtl';
  const { videoEnabled, toggleVideoEnabled, isVideoToggling } = useLocalVideoToggle();
  const lastClickTimeRef = useRef(0);
  const { hasVideoInputDevices } = useDevices();

  const toggleVideo = useCallback(() => {
    if (Date.now() - lastClickTimeRef.current > 500) {
      lastClickTimeRef.current = Date.now();
      toggleVideoEnabled();
    }
  }, [toggleVideoEnabled]);

  const tooltipMessage = useMemo(() => {
    let tooltipMessage = '';

    if (withTooltip) {
      tooltipMessage = t('camera');
    }

    return tooltipMessage;
  }, [withTooltip, t]);

  const sx = useMemo(
    () => ({
      backgroundColor: `${videoEnabled ? 'rgb(0 0 0 / 50%)' : '#FF3D3D'} !important`,
      '&:hover': {
        backgroundColor: videoEnabled ? 'rgb(0 0 0 / 50%)' : '#FF3D3D',
      },
    }),
    [videoEnabled],
  );

  return (
    <ControlTooltip title={tooltipMessage} isDisabled={!videoEnabled}>
      <Box component='span' display='block' width={withLabel ? '100%' : undefined}>
        <IconButton
          disabled={!hasVideoInputDevices || disabled || isVideoToggling}
          className={classnames(
            { [styles.deviceButton]: !withLabel, [styles.deviceButtonWithLabel]: withLabel },
            className,
          )}
          sx={
            !withLabel
              ? sx
              : {
                  justifyContent: 'flex-start',
                  width: '100%',
                }
          }
          onClick={toggleVideo}
          aria-label={t('aria.toggleCamera')}
        >
          {isVideoToggling ? (
            <Box display='flex' alignItems='center' justifyContent='center' width={24} height={24}>
              <CircularProgress size={16} thickness={5} sx={{ color: '#fff' }} />
            </Box>
          ) : (
            <>
              {videoEnabled ? (
                <VideoIcon
                  className={classnames(styles.icon, iconClassName)}
                  sx={withLabel ? { color: '#3EB157' } : {}}
                />
              ) : (
                <VideoOffIcon
                  className={classnames(styles.icon, iconClassName)}
                  sx={withLabel ? { color: '#FF3D3D' } : {}}
                />
              )}
            </>
          )}
          {withLabel && (
            <Typography
              component='p'
              variant='body2'
              sx={{
                color: 'var(--app-text-default)',
                flex: 1,
                minWidth: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'keep-all',
                overflowWrap: 'normal',
                textAlign: isRtl ? 'right' : 'left',
                paddingInlineStart: '10px',
              }}
            >
              {t('camera')}
            </Typography>
          )}
        </IconButton>
      </Box>
    </ControlTooltip>
  );
}
